import useApi from '~/composables/useApi';
import vacanciesPosts from './vacanciesPosts.gql';
import submitVacancyForm from './vacancyForm.gql';
import { VacanciesPost, PageInfo, VacancyFormInput, VacanciesFilterInput, VacancyFormOutput } from './types';
import { Logger } from '~/helpers/logger';
import { Ref, ref } from '@nuxtjs/composition-api';
import { generateVacancyFormData } from './helpers/vacancyFormDataGenerator';

interface VacanciesPosts {
    total_count: number,
    items: Array<VacanciesPost>,
    pageInfo: PageInfo,
}

const errorsFactory = () => ({
    submitForm: null,
});

const error: Ref = ref(errorsFactory());

const resetErrorValue = () => {
    error.value = errorsFactory();
};

export const useVacancies = () => {
    const { query, mutate } = useApi();
    const loadingVacancies = ref(false);
    const loadingVacancy = ref(false);
    const loadingVacancyForm = ref(false);

    const getVacancies = async (searchParams): Promise<VacanciesPosts> => {
        loadingVacancies.value = true;
        const { data } = await query<{ vacancies: VacanciesPosts }>(vacanciesPosts, searchParams);
        const vacanciesData = data?.vacancies;
        // Logger.debug(`Vacancies/Response`, vacanciesData);

        loadingVacancies.value = false;

        return {
            items: vacanciesData.items,
            total_count: vacanciesData.total_count,
            pageInfo: vacanciesData.pageInfo,
        };
    };

    const getVacancy = async (searchParams: VacanciesFilterInput): Promise<VacanciesPost> => {
        loadingVacancy.value = true;

        const { data } = await query<{ vacancies: VacanciesPosts }>(vacanciesPosts, searchParams);
        const vacanciesData = data?.vacancies;

        // Logger.debug(`Vacancy/Response`, vacanciesData);

        const vacancy_id = vacanciesData?.items[0]?.vacancy_id;
        const enabled = vacanciesData?.items[0]?.enabled;
        const name = vacanciesData?.items[0]?.name;
        const content = vacanciesData?.items[0]?.content;
        const image = vacanciesData?.items[0]?.image;
        const url_key = vacanciesData?.items[0]?.url_key;
        const location = vacanciesData?.items[0]?.location;
        const created_at = vacanciesData?.items[0]?.created_at;
        const gallery = vacanciesData?.items[0]?.gallery;

        loadingVacancy.value = false;

        return {
            vacancy_id,
            enabled,
            name,
            content,
            location,
            image,
            gallery,
            url_key,
            created_at,
        };
    };

    const submitForm = async (cv: VacancyFormInput): Promise<any> => {
        // Logger.debug('Vacancies/submitForm', cv);

        try {
            loadingVacancyForm.value = true;
            // eslint-disable-next-line no-underscore-dangle
            const input = generateVacancyFormData(cv);

            await mutate(submitVacancyForm, { VacancyFormInput: input });

            error.value.submitForm = null;
        } catch (err) {
            error.value.submitForm = err;
            Logger.error('Vacancies/submitForm', err);
        } finally {
            loadingVacancyForm.value = false;
        }
    };

    return {
        getVacancies,
        getVacancy,
        loadingVacancies,
        loadingVacancy,
        loadingVacancyForm,
        submitForm,
    };
}

export default useVacancies;